/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type { CampaignMessage } from "@packages/sdk";
import { useRequestCampaignMessage } from "@packages/sdk";
import { Close as RadixUIDialogClose } from "@radix-ui/react-dialog";
import { useState } from "react";
import { useForm } from "react-hook-form";

import type { ThemeType } from "../../../../../../global/emotion";
import { spacing } from "../../../../../../global/emotion";
import { useIsDesktopViewport } from "../../../../../hooks/useIsDesktopViewport";
import { useThemeValues } from "../../../../../hooks/useThemeValues";
import { TextArea } from "../../../../blocks/forms/TextArea/TextArea.emotion";
import { Form } from "../../../../blocks/forms/wrapper/Form.emotion";
import { FormField } from "../../../../blocks/forms/wrapper/FormField.emotion";
import { ButtonLink } from "../../../../refactor/ButtonLink/ButtonLink.emotion";
import { Button } from "../../../_core/Button/Button.emotion";
import { IconButton } from "../../../_core/IconButton/IconButton.emotion";
import { Text } from "../../../_core/Text/Text.emotion";
import { CheckmarkIcon } from "../../../icons/Icon/GeneralIcons";
import { CloseIcon, ExportIcon } from "../../../icons/Icon/NavigationIcons";

const formStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexWrap: "wrap",
  gap: spacing.m,
  width: spacing.full,
};
const headerStyle: Interpolation = {
  display: "flex",
  justifyContent: "space-between",
  width: spacing.full,
};
const footerStyle: Interpolation = {
  display: "flex",
  gap: spacing.s,
  justifyContent: "end",
};
const footerMobileStyle: Interpolation<ThemeType> = (theme) => ({
  background: theme.semanticColors.background,
  bottom: 0,
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  justifyContent: "center",
  left: 0,
  paddingBottom: spacing.ms,
  paddingInline: spacing.ml,
  position: "fixed",
  width: spacing.full,
});
const successStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
  textAlign: "center",
};
const successButtonStyle: Interpolation = {
  justifyContent: "center",
  textAlign: "center",
  width: "100%",
};

export type CampaignSupportFormProps = {
  campaignId: number;
};

enum CampaignSupportFormStep {
  Note,
  Success,
}

export const CampaignSupportForm = ({
  campaignId,
}: CampaignSupportFormProps) => {
  const t = useTranslations();
  const isDesktop = useIsDesktopViewport();
  const [step, setStep] = useState<CampaignSupportFormStep>(
    CampaignSupportFormStep.Note,
  );
  const theme = useThemeValues();

  const { mutationPost: campaignSupportMutation } = useRequestCampaignMessage({
    id: campaignId,
    onSuccess: () => setStep(CampaignSupportFormStep.Success),
  });

  const form = useForm<CampaignMessage>();

  const onSubmit = (data: CampaignMessage) => {
    campaignSupportMutation.mutate({
      text: data.text,
      type: "note_of_support",
    });
  };

  return (
    <Form
      formCSS={formStyle}
      formProps={{
        onSubmit: form.handleSubmit(onSubmit),
      }}
      {...form}
    >
      {step === CampaignSupportFormStep.Note && (
        <>
          <div css={headerStyle}>
            <Text size="m" type="headline">
              {t("note_of_support_input_title")}
            </Text>
            {isDesktop && (
              <RadixUIDialogClose asChild>
                <IconButton icon={<CloseIcon />} variant="transparentPrimary" />
              </RadixUIDialogClose>
            )}
          </div>
          <Text size="m" type="body">
            {t("campaign_notes_of_support_prompt_web")}
          </Text>
          <FormField
            control={form.control}
            name="text"
            description={
              <ButtonLink
                size="s"
                linkProps={{
                  href: "https://hallow.com/community-guidelines/",
                  target: "_blank",
                }}
                buttonProps={{ endIcon: <ExportIcon /> }}
              >
                {t("dialog_report_guidelines_button")}
              </ButtonLink>
            }
            render={({ field }) => (
              <TextArea
                maxLength={280}
                placeholder={t("note_of_support_input_placeholder")}
                rows={4}
                {...field}
              />
            )}
          />
          <div css={isDesktop ? footerStyle : footerMobileStyle}>
            <Button
              type="submit"
              disabled={campaignSupportMutation.isPending}
              size="l"
              isFullWidth={!isDesktop}
            >
              {t("general_word_post_verb")}
            </Button>
          </div>
        </>
      )}
      {step === CampaignSupportFormStep.Success && (
        <div css={successStyle}>
          <CheckmarkIcon.FilledOn
            size={60}
            type="filled"
            fill={theme.semanticColors.successSuccess}
          />
          <Text size="m" type="headline">
            {t("campaign_notes_of_support_thanks_title")}
          </Text>
          <Text size="m" type="body">
            {t("campaign_notes_of_support_thanks_subtitle")}
          </Text>
          <RadixUIDialogClose asChild>
            <Button size="l" css={successButtonStyle}>
              {t("general_word_done")}
            </Button>
          </RadixUIDialogClose>
        </div>
      )}
    </Form>
  );
};
